import { useContext, useMemo } from 'react';

import {
  AuthContext,
  CommonContext,
  timestampByUTC,
} from '@repo-level/core-front';
import { Country } from 'helpers/conf';
import { LocalStorage } from 'helpers/localStorage';
import { getSessionId } from 'helpers/analytics';
import { groupMember } from 'helpers/user';
import { pushABTesting } from 'api/analytics';
import { Locale } from 'languages';

export const getRandomNumber = (from: number, to: number) =>
  Number((Math.random() * (to - from) + from).toFixed(3));

type ABTestingVersion<T> = {
  name: string;
  probability: number;
  component?: React.ComponentType<T> | null;
};

type ABTestingConfig<T> = {
  authorized?: boolean;
  stags?: string[];
  countries?: Country[];
  locale?: Locale[];
  userGroup?: string;
  defaultVersion: React.ComponentType<T> | string | null;
  versions: ABTestingVersion<T>[];
  turnOffTest?: boolean;
};

const StagStore = new LocalStorage<string, null>('stag', null);

const ABTestingStore = (experiment: string) =>
  new LocalStorage<{ name: string }, null>(`ab-testing__${experiment}`, null);

const getRandomVersion = <T,>(versions: ABTestingVersion<T>[]) => {
  const randomNumber = getRandomNumber(1, 100);

  const ascendingProbability = (
    versionA: ABTestingVersion<T>,
    versionB: ABTestingVersion<T>
  ) => versionA.probability - versionB.probability;

  const transformProbability = (
    version: ABTestingVersion<T>,
    index: number,
    versions: ABTestingVersion<T>[]
  ) => {
    if (index === 0) return version;

    const probability = versions
      .slice(0, index + 1)
      .reduce((sum, { probability }) => sum + probability, 0);

    return {
      ...version,
      probability: versions.length === index + 1 ? 100 : probability,
    };
  };

  const appropriateByRandomNumber = ({ probability }: ABTestingVersion<T>) =>
    probability >= randomNumber;

  return versions
    .sort(ascendingProbability)
    .map(transformProbability)
    .find(appropriateByRandomNumber);
};

export function useABTesting<T extends object>(
  experiment: string,
  config: ABTestingConfig<T>
): string | React.ComponentType<T> | null {
  const { user } = useContext(AuthContext);

  const { currentIp } = useContext(CommonContext);

  const availableForTesting = useMemo(() => {
    if (config?.turnOffTest) return null;

    const ABTestingExperimentStore = ABTestingStore(experiment);

    const savedABTesting = ABTestingExperimentStore.get();

    const savedExperiment = config.versions.find(
      ({ name }) => name === savedABTesting?.name
    );

    if (savedExperiment != null)
      return savedExperiment.component !== undefined
        ? savedExperiment.component
        : savedExperiment.name;

    const byUserGroup = (() => {
      if (config.userGroup == null) return true;

      if (user?.statuses == null) return false;

      return groupMember(config.userGroup)(user);
    })();

    const byAuthorized = (() => {
      if (config.authorized == null) return true;

      if (config.authorized && user?.id != null) return true;

      if (!config.authorized && user?.id == null) return true;

      return false;
    })();

    const byStag = (() => {
      if (config.stags == null) return true;

      const storageStag = StagStore.get();

      if (storageStag == null) return false;

      return config.stags.includes(storageStag);
    })();

    const byCountry = (() => {
      if (config.countries == null) return true;

      return config.countries.includes(currentIp?.countryCode as Country);
    })();

    const conditions = byAuthorized && byUserGroup && byStag && byCountry;

    if (conditions) {
      const version = getRandomVersion(config.versions);

      if (version == null) return config.defaultVersion;

      ABTestingExperimentStore.replace({ name: version.name });

      pushABTesting({
        experiment,
        version: version.name,
        attributes: {
          sessionId: getSessionId(),
          userId: user?.id,
          createdAt: timestampByUTC(),
          updatedAt: timestampByUTC(),
        },
      });

      return version.component !== undefined ? version.component : version.name;
    }

    return config.defaultVersion;
  }, [experiment, config, user, currentIp]);

  return availableForTesting;
}
